import { useLocation, useNavigate, Link } from "@/lib/router-compat";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { logNotFound } from "@/lib/logNotFound";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    async function checkForRedirect() {
      const pathname = location.pathname;

      // Manual slug redirects (old/incorrect URLs → canonical article URLs)
      const MANUAL_REDIRECTS: Record<string, string> = {
        "/articles/review-of-hy3750i-hyundai-inverter-framed-generator-2026":
          "/articles/hyundai-hy3750i-inverter-generator-review-2026",
        "/category/pruning-training": "/category/how-to?sub=pruning",
        "/category/pruning-and-training": "/category/how-to?sub=pruning",
        "/category/space-heaters": "/space-heaters",
      };
      const cleanedForManual = pathname.replace(/\/+$/, "");
      if (MANUAL_REDIRECTS[cleanedForManual]) {
        navigate(MANUAL_REDIRECTS[cleanedForManual], { replace: true });
        return;
      }

      // Block WordPress resource URLs and known-bad paths - show 404 immediately
      const KNOWN_BAD_PATHS = new Set([
        "/articles/yes-they-",
        "/articles/how",
        "/pruning-and-traininh",
        "/guest-post-submission",
        "/become-a-contributor",
        "/about-2",
        "/honda-miimo-hrm-70-live-review",
        "/garden-reviews",
        "/garden-tips-advice",
        "/lawn-care",
        "/sign-up-for-lastest-at-garden-review",
      ]);
      const cleanedPath = pathname.replace(/\/+$/, "");
      if (
        pathname.startsWith("/wp-admin") ||
        pathname.startsWith("/wp-includes") ||
        pathname.startsWith("/wp-content") ||
        pathname.startsWith("/tag/") ||
        pathname.includes("/feed/") ||
        pathname.endsWith("/feed") ||
        // Resend newsletter click-tracking URLs (cause GSC soft-404s)
        /\/0102019e[0-9a-f]/i.test(pathname) ||
        // Curly-quote / smart-quote / unencoded space in URL = malformed crawler artefact
        /[″"'\u201C\u201D\u2018\u2019]/.test(pathname) ||
        // Comma / ampersand inside a /category/ slug = scraped multi-tag artefact
        (pathname.startsWith("/category/") && /[,&]/.test(pathname)) ||
        KNOWN_BAD_PATHS.has(cleanedPath)
      ) {

        setIsChecking(false);
        return;
      }

      // Normalize encoded spaces (%20) to hyphens and redirect
      const decodedPath = decodeURIComponent(pathname);
      const normalizedPath = decodedPath.replace(/\s+/g, "-");
      if (normalizedPath !== decodedPath) {
        navigate(normalizedPath, { replace: true });
        return;
      }

      // Strip trailing slash for matching
      const cleanPath = pathname.replace(/\/+$/, "");

      // Extract potential slug from various WordPress URL patterns
      // e.g., /post-slug/, /2024/01/post-slug/, /2024/01/15/post-slug/
      const pathParts = cleanPath.split("/").filter(Boolean);
      const potentialSlug = pathParts[pathParts.length - 1];

      if (!potentialSlug) {
        setIsChecking(false);
        return;
      }

      // Check if it's a category URL with trailing slash (e.g., /category/garden-life/)
      if (pathParts[0] === "category" && pathParts.length === 2) {
        navigate(`/category/${potentialSlug}`, { replace: true });
        return;
      }

      // Check if this slug exists in our articles
      const { data: article } = await supabase
        .from("articles")
        .select("slug")
        .eq("slug", potentialSlug)
        .eq("status", "published")
        .maybeSingle();

      if (article) {
        navigate(`/articles/${article.slug}`, { replace: true });
        return;
      }

      // Check if this slug exists in our pages
      const { data: page } = await supabase
        .from("pages")
        .select("slug")
        .eq("slug", potentialSlug)
        .eq("status", "published")
        .maybeSingle();

      if (page) {
        navigate(`/${page.slug}`, { replace: true });
        return;
      }

      // Try matching slug without trailing numbers (e.g., "about-2" -> "about")
      const baseSlug = potentialSlug.replace(/-\d+$/, '');
      if (baseSlug !== potentialSlug) {
        const { data: basePage } = await supabase
          .from("pages")
          .select("slug")
          .eq("slug", baseSlug)
          .eq("status", "published")
          .maybeSingle();

        if (basePage) {
          navigate(`/${basePage.slug}`, { replace: true });
          return;
        }

        // Also try base slug as article
        const { data: baseArticle } = await supabase
          .from("articles")
          .select("slug")
          .eq("slug", baseSlug)
          .eq("status", "published")
          .maybeSingle();

        if (baseArticle) {
          navigate(`/articles/${baseArticle.slug}`, { replace: true });
          return;
        }
      }

      // No redirect found, show 404
      setIsChecking(false);
      void logNotFound("notfound", pathname);
      console.error(
        "404 Error: User attempted to access non-existent route:",
        pathname
      );
    }

    checkForRedirect();
  }, [location.pathname, navigate]);

  if (isChecking) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <title>Page Not Found | Garden-Review</title>
        <meta name="description" content="This page could not be found. Browse Garden-Review for UK garden tool reviews, buying guides and growing advice." />
        </Helmet>
        <Header />
        <main className="flex-1 flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Page Not Found | Garden-Review</title>
        <meta name="description" content="This page could not be found. Browse Garden-Review for UK garden tool reviews, buying guides and growing advice." />
      </Helmet>
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center py-24">
          <div className="text-center max-w-xl px-4">
            <h1 className="text-6xl font-bold text-primary mb-4">404</h1>
            <p className="text-xl text-muted-foreground mb-6">
              Oops! The page you're looking for doesn't exist.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button asChild>
                <Link to="/">Return to Home</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/articles">Browse Articles</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/browse">Browse All Pages</Link>
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default NotFound;
